import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useBoolean } from "@/hooks/use-boolean";
import { useReturnValidationContext } from "@/pages/(app)/returns/view/utils/return-validation-context";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  MultiButton,
  MultiButtonItem,
} from "@babackapp/ui/components/multi-button";
import { Loader2 } from "lucide-react";
import { RejectLineItemsProvider } from "../../../refund/hooks/use-reject-line-items-context";
import { RejectLineItemsStateProvider } from "../../../refund/hooks/use-reject-line-items-state-context";
import ConfirmReturnDialog from "../confirm-return/confirm-return-dialog";
import RejectReturnDialog from "../reject-return/reject-return-dialog";
import CancelReturnAlertDialog from "../return-nav/cancel-return-alert-dialog";
import { useAuthorizeAction } from "./hooks/use-authorize-action";
import { useCancelAction } from "./hooks/use-cancel-action";
import { useMarkAsInTransitAction } from "./hooks/use-mark-as-in-transit-action";
import { useMarkAsValidatedAction } from "./hooks/use-mark-as-validated-action";
import { useMarkAsReceivedAction } from "./hooks/use-marked-as-received-action";
import { useRegenerateAction } from "./hooks/use-regenerate-action";
import { useRejectItemsAction } from "./hooks/use-reject-items-action";
import { useRejectReturnRequestAction } from "./hooks/use-reject-return-request-action";
import { useReopenAction } from "./hooks/use-reopen-action";
import { useValidateAction } from "./hooks/use-validate-action";
import { useOpenCarrierClaimAction } from "./hooks/use-open-carrier-claim-action";

type Action = {
  label: string;
  icon?: React.ReactNode;
  description?: string;
  enabled: boolean;
  onClick: () => void;
  loading?: boolean;
};

export function ReturnRequestButton() {
  const loadingAsync = useBoolean(false);
  const {
    isOpen: confirmDialogOpen,
    openDialog,
    closeDialog,
  } = useReturnValidationContext();
  const rejectDialogOpen = useBoolean(false);
  const cancelAlertOpen = useBoolean(false);
  const t = useTranslation();
  const isRetractionOnly = useIsRetractionOnly();
  const reopen = useReopenAction();
  const markAsValidated = useMarkAsValidatedAction();
  const markAsReceived = useMarkAsReceivedAction();
  const markAsInTransit = useMarkAsInTransitAction();
  const regenerate = useRegenerateAction();
  const authorize = useAuthorizeAction();
  const rejectReturnRequest = useRejectReturnRequestAction();
  const cancel = useCancelAction(cancelAlertOpen.setTrue);
  const validate = useValidateAction(() => openDialog());
  const rejectItems = useRejectItemsAction(rejectDialogOpen.setTrue);
  const openCarrierClaimAction = useOpenCarrierClaimAction();

  const menu: Action[] = [
    reopen,
    markAsValidated,
    ...(!isRetractionOnly
      ? [markAsReceived, markAsInTransit, regenerate, openCarrierClaimAction]
      : []),
    cancel,
  ].filter((item) => item.enabled);

  const loading = menu.some((item) => item.loading);
  const authorizeOrRejectLoading =
    authorize.loading || rejectReturnRequest.loading;

  if (loading || authorizeOrRejectLoading) {
    return <ButtonLoading isLoading />;
  }

  return (
    <>
      {validate.enabled && (
        <Button
          onClick={validate.onClick}
          aria-label={validate.label}
          disabled={loadingAsync.value}
        >
          {loadingAsync.value && <Loader2 className="animate-spin" />}
          {validate.label}
        </Button>
      )}

      {rejectItems.enabled && (
        <Button
          variant="secondary"
          onClick={rejectItems.onClick}
          aria-label={rejectItems.label}
          disabled={loadingAsync.value}
        >
          {loadingAsync.value && <Loader2 className="animate-spin" />}
          {rejectItems.label}
        </Button>
      )}

      {authorize.enabled && (
        <Button
          onClick={authorize.onClick}
          aria-label={authorize.label}
          disabled={authorizeOrRejectLoading}
        >
          {authorizeOrRejectLoading ? (
            <Loader2 className="animate-spin" />
          ) : (
            authorize.label
          )}
        </Button>
      )}

      {rejectReturnRequest.enabled && (
        <Button
          onClick={rejectReturnRequest.onClick}
          variant="secondary"
          aria-label={rejectReturnRequest.label}
          disabled={authorizeOrRejectLoading}
        >
          {authorizeOrRejectLoading ? (
            <Loader2 className="animate-spin" />
          ) : (
            rejectReturnRequest.label
          )}
        </Button>
      )}

      {menu.length > 0 && (
        <MultiButton variant="secondary" label={t("misc.edit")}>
          {menu.map((item) => (
            <MultiButtonItem
              key={item.label}
              label={item.label}
              icon={item.icon}
              description={item.description}
              onClick={item.onClick}
            />
          ))}
        </MultiButton>
      )}

      <ConfirmReturnDialog
        open={confirmDialogOpen}
        setOpen={(open) => {
          if (!open) closeDialog();
        }}
        setLoading={loadingAsync.setValue}
      />
      <RejectLineItemsProvider>
        <RejectLineItemsStateProvider>
          <RejectReturnDialog
            open={rejectDialogOpen.value}
            setOpen={rejectDialogOpen.setValue}
            setLoading={loadingAsync.setValue}
          />
        </RejectLineItemsStateProvider>
      </RejectLineItemsProvider>
      <CancelReturnAlertDialog
        isOpen={cancelAlertOpen.value}
        onOpenChange={cancelAlertOpen.setValue}
      />
    </>
  );
}
